import { regexForm } from 'components/utils/regexForm'
import { validationCNPJ, validationCPF } from 'components/utils/validationsFN'
import type { ContractMode } from 'contexts/contract/interface'
import formatter from 'utils/formatter'
import validation from 'utils/validation'
import * as yup from 'yup'
import { createChassiSchema } from './Utils/chassiSchema'

const booleanType = (str: string) => str === 'SIM'
const registroCPF = (str: string) => str === 'CPF'
const registroCNPJ = (str: string) => str === 'CNPJ'
const checkPorcent = (str: string) => /%/.test(str)

export const schema = (type: ContractMode = 'CREATE') => yup
    .object({
        vehicles: yup.object().shape(
            {
                chassi: createChassiSchema(type),
                renavam: yup.string().test({
                    name: 'invalid',
                    test: value =>
                        value?.length
                            ? !!value && validation.verifiedRenavam(value)
                            : true
                }),
                uf: yup.string(),
                ufGravame: yup.string().required(),
                placa: yup
                    .string()
                    .transform(value => formatter.placa(value))
                    .test({
                        name: 'invalid',
                        test: value => (value ? validation.placa(value) : true)
                    }),
                numeroGravame: yup
                    .string()
                    .trim()
                    .required()
                    .max(8, 'Máximo de 8 caracteres'),
                anoFabricacao: yup
                    .number()
                    .required()
                    .test({
                        name: 'invalid',
                        test: value => !!value && validation.ano(value)
                    }),
                anoModelo: yup
                    .number()
                    .required()
                    .test({
                        name: 'invalid',
                        test: value => !!value && validation.ano(value)
                    }),
                identificacaoRemarcacao: yup.number(),
                cor: yup
                    .string()
                    .when('ufGravame', {
                        is: 'RJ',
                        then: yup
                            .string()
                            .required('Campo obrigatório')
                            .max(25, 'Máximo de 25 caracteres')
                    })
                    .when('cor', {
                        is: (exists: any) => {
                            return exists !== undefined
                        },
                        then: yup.string().max(10, 'Máximo de 10 caracteres'),
                        otherwise: yup.string()
                    }),
                marca: yup
                    .string()
                    .when('ufGravame', {
                        is: 'RJ',
                        then: yup
                            .string()
                            .required('Campo obrigatório')
                            .max(25, 'Máximo de 25 caracteres')
                    })
                    .when('marca', {
                        is: (exists: any) => {
                            return exists !== undefined
                        },
                        then: yup.string().max(25, 'Máximo de 25 caracteres'),
                        otherwise: yup.string()
                    }),
                especie: yup.number().required(),
                indicativoVeiculoNovo: yup
                    .string()
                    .required('Campo obrigatório'),
                numeroMotor: yup.string().max(21, 'Máximo de 21 caracteres')
            },
            [
                ['cor', 'cor'],
                ['marca', 'marca']
            ]
        ),
        vehiclesList: yup.array().of(
            yup.object({ chassi: createChassiSchema(type) })
        ),
        complementary: yup.object({
            identificadorTaxaMora: yup.string(),
            fundoReserva: yup
                .string()
                .nullable(true)
                .max(15, 'Valor máximo R$ 9.999.999,99')
                .transform(value =>
                    value !== undefined && value !== null
                        ? formatter.moeda(value)
                        : null
                ),

            valorTaxaMora: yup
                .string()
                .max(12, 'Valor máximo 999.999,999%')
                .transform(value => formatter.percentual(value))
                .when('identificadorTaxaMora', {
                    is: booleanType,
                    then: schema => schema.required(),
                    otherwise: schema =>
                        schema.nullable(true).transform(() => null)
                }),
            identificadorTaxaMulta: yup.string(),
            valorTaxaMulta: yup
                .string()
                .max(8, 'Valor máximo 999,999%')
                .transform(value => formatter.percentual(value))
                .when('identificadorTaxaMulta', {
                    is: booleanType,
                    then: schema => schema.required(),
                    otherwise: schema =>
                        schema.nullable(true).transform(() => null)
                }),
            valorIOF: yup
                .string()
                .nullable(true)
                .max(15, 'Valor máximo R$ 9.999.999,99')
                .transform(value => (value ? formatter.moeda(value) : null)),
            valorTaxaJurosMes: yup
                .string()
                .nullable(true)
                .max(8, 'Valor máximo 999,999%')
                .transform(value =>
                    value ? formatter.percentual(value) : null
                ),
            valorTaxaJurosAno: yup
                .string()
                .nullable(true)
                .max(8, 'Valor máximo 999,999%')
                .transform(value =>
                    value ? formatter.percentual(value) : null
                ),
            valorTaxaContrato: yup
                .string()
                .nullable(true)
                .max(15, 'Valor máximo R$ 9.999.999,99')
                .transform(value => (value ? formatter.moeda(value) : null)),
            indices: yup
                .string()
                .trim()
                .required()
                .max(10, 'Máximo de 10 caracteres'),
            identificadorComissao: yup.string().required(),
            valorComissao: yup
                .string()
                .when('identificadorComissao', {
                    is: booleanType,
                    then: schema => schema.required(),
                    otherwise: schema =>
                        schema.nullable(true).transform(() => null)
                })
                .when({
                    is: checkPorcent,
                    then: schema => schema.max(8, 'Valor máximo 999,999%'),
                    otherwise: schema =>
                        schema.max(15, 'Valor máximo R$ 9.999.999,99')
                }),
            penalidade: yup
                .string()
                .max(50, 'Máximo de 50 caracteres')
                .when('indicacaoPenalidade', {
                    is: booleanType,
                    then: schema => schema.required().trim(),
                    otherwise: schema =>
                        schema.nullable(true).transform(() => null)
                }),
            indicacaoPenalidade: yup.string().required(),
            principalRecebedorPagamento: yup
                .string()
                .trim()
                .max(40, 'Máximo de 40 caracteres'),
            tipoDocumentoRecebedor: yup.string(),
            documentoPrincipalRecebedorPagamento: yup
                .string()
                .when('tipoDocumentoRecebedor', {
                    is: registroCPF,
                    then: schema =>
                        schema
                            .transform(value => formatter.cpf(value))
                            .matches(regexForm.cpf)
                            .test({
                                name: 'invalid',
                                test: value =>
                                    !!value && validationCPF(value as string)
                            }),
                    otherwise: schema => schema.nullable()
                })
                .when('tipoDocumentoRecebedor', {
                    is: registroCNPJ,
                    then: schema =>
                        schema
                            .transform(value => formatter.cnpj(value))
                            .matches(regexForm.cnpj)
                            .test({
                                name: 'invalid',
                                test: value =>
                                    !!value && validationCNPJ(value as string)
                            }),
                    otherwise: schema => schema.nullable()
                }),
            tipoDocumentoVendedor: yup.string(),
            documentoVendedor: yup
                .string()
                .when('tipoDocumentoVendedor', {
                    is: registroCPF,
                    then: schema =>
                        schema
                            .transform(value => formatter.cpf(value))
                            .matches(regexForm.cpf)
                            .test({
                                name: 'invalid',
                                test: value =>
                                    !!value && validationCPF(value as string)
                            }),
                    otherwise: schema => schema.nullable()
                })
                .when('tipoDocumentoVendedor', {
                    is: registroCNPJ,
                    then: schema =>
                        schema
                            .transform(value => formatter.cnpj(value))
                            .matches(regexForm.cnpj)
                            .test({
                                name: 'invalid',
                                test: value =>
                                    !!value && validationCNPJ(value as string)
                            }),
                    otherwise: schema => schema.nullable()
                }),
            numeroCertidaoNegativaDebitos: yup
                .string()
                .nullable(true)
                .max(60, 'Máximo de 60 caracteres')
                .transform(v => (v ? String(v).trim() : null)),
            comentarios: yup
                .string()
                .max(400, 'Máximo de 400 caracteres')
                .nullable(),
            correcaoMonetaria: yup.string()
        }),
        contract: yup.object().shape(
            {
                tipoAditivo: yup.string().nullable(true),
                opcaoCompra: yup
                    .string()
                    .nullable(true)
                    .when(['ufContrato', 'tipoRestricao'], {
                        is: (ufContrato: any, tipoRestricao: any) =>
                            ufContrato === 'RS' && String(tipoRestricao) === '1',
                        then: schema => schema.required('Campo obrigatório'),
                        otherwise: schema => schema.nullable(true).transform(() => null)
                    }),
                dataContrato: yup.string().required(),
                numeroContrato: yup
                    .string()
                    .trim()
                    .required()
                    .max(20, 'Máximo de 20 caracteres'),
                tipoRestricao: yup.string().required(),
                quantidadeMeses: yup
                    .number()
                    .required()
                    .min(1, 'Os meses devem ser maior ou igual a 1')
                    .max(999, 'Os meses devem ser menor ou igual a 999'),
                numAditivoContratoOrigem: yup
                    .string()
                    .nullable(true)
                    .test(
                        'numero-aditivo-valido',
                        'Informe somente números',
                        value => !value || /^\d+$/.test(value)
                    ),
                numeroRegistroContrato: yup
                    .string()
                    .nullable(true)
                    .test(
                        'numero-registro-contrato-valido',
                        'Use no máximo 30 caracteres: letras, números, ponto, barra, hífen ou sublinhado',
                        value =>
                            !value ||
                            /^[A-Za-z0-9./_-]{1,30}$/.test(value.trim())
                    ),
                numeroDudaPago: yup.string().when('numeroDudaPago', {
                    is: (exists: any) => {
                        return exists !== undefined
                    },
                    then: yup
                        .string()
                        .transform(value => formatter.onlyNumber(value))
                        .max(10, 'Máximo de 10 caracteres'),
                    otherwise: yup.string()
                })
            },
            [['numeroDudaPago', 'numeroDudaPago']]
        ),
        financing: yup
            .object()
            .shape(
                {
                    valorTotalDivida: yup
                        .string()
                        .required(
                            'Valor total da dívida  é um campo obrigatório'
                        )
                        .max(15, 'Valor máximo R$ 9.999.999,99')
                        .transform(value =>
                            value ? formatter.moeda(value) : ''
                        ),
                    valorParcela: yup
                        .string()
                        .max(15, 'Valor máximo R$ 9.999.999,99')
                        .transform(value =>
                            value !== undefined &&
                                value !== null &&
                                value !== ''
                                ? formatter.moeda(value)
                                : ''
                        ),
                    dataLiberacaoCredito: yup
                        .string()
                        .required(
                            'Data Liberação Crédito é um campo obrigtório'
                        ),
                    ufLiberacaoCredito: yup
                        .string()
                        .required(
                            'UF Liberação de Crédito é um campo obrigtório'
                        ),
                    municipioLiberacaoCredito: yup
                        .string()
                        .required(
                            'Município Liberação de Crédito é um campo obrigtório'
                        ),
                    vencimentoPrimeiraParcela: yup
                        .mixed<string>()
                        .concat(yup.date())
                        .transform((curr, orig) => (orig === '' ? null : curr))
                        .when(
                            'vencimentoUltimaParcela',
                            (vencimentoUltimaParcela, schema) => {
                                return vencimentoUltimaParcela
                                    ? schema.max(
                                        vencimentoUltimaParcela,
                                        'A 1ª parcela não pode ser superior à última parcela'
                                    )
                                    : schema
                            }
                        )
                        .test({
                            name: 'same',
                            message: 'Não são permitidas datas iguais',
                            test: function (value) {
                                const startDate = formatter.data(
                                    this.parent.vencimentoUltimaParcela
                                )
                                const endDate = formatter.data(value as any)
                                return !(startDate === endDate)
                            }
                        })
                        .nullable()
                        .required(),
                    vencimentoUltimaParcela: yup
                        .mixed<string>()
                        .concat(yup.date())
                        .transform((curr, orig) => (orig === '' ? null : curr))
                        .when(
                            'vencimentoPrimeiraParcela',
                            (vencimentoPrimeiraParcela, schema) => {
                                return vencimentoPrimeiraParcela
                                    ? schema.min(
                                        vencimentoPrimeiraParcela,
                                        'A última parcela não pode ser inferior à 1ª parcela'
                                    )
                                    : schema
                            }
                        )
                        .test({
                            name: 'same',
                            message: 'Não são permitidas datas iguais',
                            test: function (value) {
                                const startDate = formatter.data(
                                    this.parent.vencimentoPrimeiraParcela
                                )
                                const endDate = formatter.data(value as any)
                                return !(startDate === endDate)
                            }
                        })
                        .nullable()
                        .required()
                },
                [['vencimentoPrimeiraParcela', 'vencimentoUltimaParcela']]
            )
            .required(),
        consortium: yup.object({
            cotaConsorcio: yup
                .string()
                .nullable()
                .max(6, 'Máximo de 6 caracteres'),
            numeroGrupo: yup
                .string()
                .nullable()
                .max(6, 'Máximo de 6 caracteres')
        }),
        creditor: yup
            .object({
                cnpj: yup.string().required(),
                email: yup.string().email(),
                razaoSocial: yup.string().max(128, 'Máximo de 128 caracteres'),
                telefone: yup
                    .string()
                    .required()
                    .transform(value => formatter.contato(value))
                    .matches(regexForm.contato),
                endereco: yup.object({
                    cep: yup
                        .string()
                        .required()
                        .min(9, 'Mínimo de 8 caracteres'),
                    uf: yup.string().required(),
                    municipio: yup.string().required(),
                    logradouro: yup.string().trim().required(),
                    bairro: yup.string().trim().required(),
                    complemento: yup
                        .string()
                        .max(20, 'Máximo de 20 caracteres'),
                    numero: yup
                        .string()
                        .max(5, 'Máximo de 5 caracteres')
                        .trim()
                        .required()
                })
            })
            .required(),
        debtor: yup
            .object({
                nome: yup
                    .string()
                    .trim()
                    .max(40, 'Máximo de 40 caracteres')
                    .required(),
                tipoDocumento: yup.string().required(),
                email: yup.string().email(),
                documento: yup
                    .string()
                    .required()
                    .when('tipoDocumento', {
                        is: registroCPF,
                        then: schema =>
                            schema
                                .transform(value => formatter.cpf(value))
                                .matches(regexForm.cpf)
                                .test({
                                    name: 'invalid',
                                    test: value =>
                                        !!value &&
                                        validationCPF(value as string)
                                }),
                        otherwise: schema =>
                            schema
                                .transform(value => formatter.cnpj(value))
                                .matches(regexForm.cnpj)
                                .test({
                                    name: 'invalid',
                                    test: value =>
                                        !!value &&
                                        validationCNPJ(value as string)
                                })
                    }),
                telefone: yup
                    .string()
                    .required()
                    .transform(value => formatter.contato(value))
                    .matches(regexForm.contato),
                endereco: yup.object({
                    cep: yup
                        .string()
                        .required()
                        .min(9, 'Mínimo de 8 caracteres'),
                    complemento: yup
                        .string()
                        .max(20, 'Máximo de 20 caracteres'),
                    uf: yup.string().required(),
                    municipio: yup.string().required(),
                    logradouro: yup.string().trim().required(),
                    bairro: yup.string().trim().required(),
                    numero: yup
                        .string()
                        .max(5, 'Máximo de 5 caracteres')
                        .trim()
                        .required()
                })
            })
            .required(),
        terceiroGarantidor: yup.object({
            nome: yup
                .string()
                .nullable(true)
                .max(128, 'Máximo de 128 caracteres'),

            email: yup
                .string()
                .nullable(true)
                .email('Email inválido'),

            tipoDocumento: yup
                .string()
                .nullable(true),

            documento: yup
                .string()
                .nullable(true)
                .test({
                    name: 'documento-terceiro-garantidor-valido',
                    message: 'Documento inválido',
                    test: function (value) {
                        if (!value) return true

                        const tipoDocumento = this.parent.tipoDocumento
                        const documento = formatter.cleanFormatter(value)

                        if (tipoDocumento === 'CPF') {
                            return validationCPF(documento)
                        }

                        if (tipoDocumento === 'CNPJ') {
                            return validationCNPJ(documento)
                        }

                        return true
                    }
                }),

            telefone: yup
                .string()
                .nullable(true)
                .test({
                    name: 'telefone-terceiro-garantidor-valido',
                    message: 'Telefone inválido',
                    test: value => {
                        if (!value) return true
                        return regexForm.contato.test(formatter.contato(value))
                    }
                }),

            endereco: yup.object({
                cep: yup
                    .string()
                    .nullable(true)
                    .test({
                        name: 'cep-terceiro-garantidor-valido',
                        message: 'Mínimo de 8 caracteres',
                        test: value => !value || value.length >= 9
                    }),
                uf: yup.string().nullable(true),
                municipio: yup.string().nullable(true),
                logradouro: yup.string().nullable(true),
                bairro: yup.string().nullable(true),
                numero: yup
                    .string()
                    .nullable(true)
                    .max(5, 'Máximo de 5 caracteres'),
                complemento: yup
                    .string()
                    .nullable(true)
                    .max(20, 'Máximo de 20 caracteres')
            })
        })
    })
    .test(
        'valor-taxa-contrato-obrigatorio-pr',
        'Campo obrigatório',
        function (value?: any) {
            const isPR = value?.vehicles?.ufGravame === 'PR'
            const valorTaxaContrato = value?.complementary?.valorTaxaContrato

            if (isPR && !valorTaxaContrato) {
                return this.createError({
                    path: 'complementary.valorTaxaContrato',
                    message: 'Campo obrigatório'
                })
            }

            return true
        }
    )
    .test(
        'consortium-required',
        'Consortium fields are required when type is Consorcio',
        function (value?: any) {
            const TYPE_CONSORCIO = '6'

            if (value.contract?.tipoRestricao === TYPE_CONSORCIO) {
                const errors = []

                if (!value.consortium?.cotaConsorcio) {
                    errors.push(
                        this.createError({
                            path: 'consortium.cotaConsorcio',
                            message: 'Campo obrigatório'
                        })
                    )
                }

                if (!value.consortium?.numeroGrupo) {
                    errors.push(
                        this.createError({
                            path: 'consortium.numeroGrupo',
                            message: 'Campo obrigatório'
                        })
                    )
                }

                return errors.length === 0
                    ? true
                    : new yup.ValidationError(errors)
            }

            return true
        }
    )
